import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { PROMO_TYPES, promoBadge } from "@/lib/promotions";
import { ArrowLeft, Plus, Trash2, Tag, Pencil, Eye, MousePointerClick, Check, X, Upload, GripVertical, Image } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";

const BLANK = {
  title: "",
  promo_type: "special_buy",
  scope: "manufacturer",
  make: "",
  model: "",
  partner_id: "",
  vehicle_ids: [],
  badge_label: "",
  description: "",
  terms: "",
  start_date: "",
  end_date: "",
  logo_url: "",
  display_format: "text",
  banner_destination: "vehicle",
  banner_hide_overlay: false,
  linked_vehicle_id: "",
  banner_image_id: "",
  banner_image_url: "",
  active: true,
  priority: 0,
};

export default function StaffPromotions() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [partners, setPartners] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | promo object
  const [form, setForm] = useState(BLANK);
  const [confirmDel, setConfirmDel] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dragId, setDragId] = useState(null);
  const bannerRef = useRef(null);
  const [bannerBusy, setBannerBusy] = useState(false);

  const load = () => api.get("/promotions").then(({ data }) => setItems((data || []).sort((a, b) => (b.priority || 0) - (a.priority || 0)))).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/partners").then(({ data }) => setPartners(data || [])).catch(() => setPartners([]));
    api.get("/vehicles").then(({ data }) => setVehicles(data || [])).catch(() => setVehicles([]));
    api.get("/promotions/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, []);

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const arr = [...items];
    const from = arr.findIndex((x) => x.id === dragId), to = arr.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1); arr.splice(to, 0, moved);
    setItems(arr); setDragId(null);
    try { await api.post("/promotions/reorder", { ids: arr.map((x) => x.id) }); toast.success("Order saved"); }
    catch { toast.error("Couldn't reorder"); load(); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const logoRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/content/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("logo_url", data.url); toast.success("Logo uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setLogoBusy(false); }
  };
  const uploadBanner = async (file) => {
    if (!file) return;
    setBannerBusy(true);
    const data = new FormData();
    data.append("file", file);
    try {
      const response = await api.post("/promotions/banner-upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("banner_image_id", response.data.id);
      set("banner_image_url", response.data.url);
      toast.success("Banner artwork uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Banner upload failed");
    } finally {
      setBannerBusy(false);
    }
  };
  const openNew = (displayFormat = "text") => {
    setForm({ ...BLANK, display_format: displayFormat });
    setEditing("new");
  };
  const openEdit = (p) => { setForm({ ...BLANK, ...p, vehicle_ids: p.vehicle_ids || [] }); setEditing(p); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Give the promotion a title"); return; }
    if (
      form.display_format === "banner" &&
      form.banner_destination === "vehicle" &&
      !form.linked_vehicle_id
    ) {
      toast.error("Choose the vehicle this banner promotes");
      return;
    }
    if (form.display_format === "banner" && (!form.start_date || !form.end_date)) {
      toast.error("Banner specials need a start and end date");
      return;
    }
    if (form.display_format === "banner" && !form.banner_image_id) {
      toast.error("Upload 3:1 banner artwork before publishing");
      return;
    }
    const payload = { ...form, vehicle_ids: typeof form.vehicle_ids === "string" ? form.vehicle_ids.split(",").map((s) => s.trim()).filter(Boolean) : form.vehicle_ids, priority: Number(form.priority) || 0 };
    try {
      if (editing === "new") await api.post("/promotions", payload);
      else await api.patch(`/promotions/${editing.id}`, payload);
      toast.success("Promotion saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save promotion"); }
  };
  const del = async (p) => {
    setConfirmDel(null); setItems((prev) => prev.filter((x) => x.id !== p.id));
    try { await api.delete(`/promotions/${p.id}`); toast.success("Promotion deleted"); }
    catch { toast.error("Couldn't delete"); load(); }
  };

  const vehicleIdsStr = Array.isArray(form.vehicle_ids) ? form.vehicle_ids.join(", ") : form.vehicle_ids;
  const approve = async (p) => {
    try {
      await api.post(`/promotions/${p.id}/approve`);
      toast.success("Promotion approved");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't approve");
    }
  };
  const reject = async (p) => { try { await api.post(`/promotions/${p.id}/reject`); toast.success("Promotion rejected"); load(); } catch { toast.error("Couldn't reject"); } };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/staff")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900" data-testid="promo-back-btn"><ArrowLeft className="h-4 w-4" /> Pipeline</button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-rose-50 text-rose-600"><Tag className="h-5 w-5" strokeWidth={2.5} /></div>
            <div>
              <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950 dark:text-slate-50">Specials & promotions</h1>
              <p className="text-sm text-slate-600 dark:text-slate-300">Lease/APR specials, special buys & rebates — shown as ribbons on matching cars.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => openNew("banner")}
              data-testid="promo-new-banner-btn"
              className="rounded-md bg-rose-600 font-bold text-white hover:bg-rose-700"
            >
              <Image className="mr-1.5 h-4 w-4" /> New banner
            </Button>
            <Button
              onClick={() => openNew("text")}
              data-testid="promo-new-btn"
              variant="outline"
              className="rounded-md border-slate-300 font-bold dark:border-slate-700"
            >
              <Plus className="mr-1.5 h-4 w-4" /> New text offer
            </Button>
          </div>
        </div>

        {analytics && (analytics.totals.views > 0 || analytics.totals.clicks > 0) && (
          <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="promo-analytics">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-slate-900 dark:text-slate-100">Engagement · last 14 days</h3>
              <div className="flex gap-4 text-sm font-bold">
                <span className="inline-flex items-center gap-1 text-slate-500"><Eye className="h-4 w-4" /> {analytics.totals.views}</span>
                <span className="inline-flex items-center gap-1 text-rose-600"><MousePointerClick className="h-4 w-4" /> {analytics.totals.clicks}</span>
              </div>
            </div>
            <div className="mt-3 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.series} margin={{ top: 4, right: 0, left: -24, bottom: 0 }}>
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} />
                  <Bar dataKey="views" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="clicks" fill="#e11d48" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {items === null ? (
          <p className="mt-10 text-slate-400">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-16 text-center" data-testid="promo-empty">
            <Tag className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-500 dark:text-slate-400">No promotions yet</p>
            <p className="text-sm text-slate-400 dark:text-slate-500">Create one to badge matching cars and light up the Landing banner.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="promo-list">
            {items.map((p) => (
              <div key={p.id} data-testid={`promo-item-${p.id}`} draggable onDragStart={() => setDragId(p.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(p.id)}
                className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-white dark:bg-slate-900 p-4 ${dragId === p.id ? "border-rose-400 opacity-60" : "border-slate-200 dark:border-slate-800"}`}>
                <div className="flex min-w-0 items-start gap-2">
                  <GripVertical className="mt-1 h-4 w-4 shrink-0 cursor-grab text-slate-300" data-testid={`promo-drag-${p.id}`} />
                  <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold ${PROMO_TYPES[p.promo_type]?.cls || "bg-rose-600 text-white"}`}>{promoBadge(p)}</span>
                    {p.display_format === "banner" && <span className="rounded-full bg-slate-950 px-2 py-0.5 text-[10px] font-bold uppercase text-white dark:bg-slate-700">Banner</span>}
                    <span className="font-display text-base font-bold text-slate-900 dark:text-slate-100">{p.title}</span>
                    {p.status === "pending" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700" data-testid={`promo-pending-${p.id}`}>Pending</span>}
                    {p.status === "rejected" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">Rejected</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.is_active_now ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"}`}>{p.is_active_now ? "Live" : "Off"}</span>
                    {p.submitted_by && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">Partner</span>}
                  </div>
                  <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
                    Scope: {p.scope}{p.scope === "manufacturer" && p.make ? ` · ${p.make}` : ""}{p.scope === "vehicle" ? ` · ${(p.vehicle_ids || []).length} car(s)` : ""}
                    {p.start_date || p.end_date ? ` · ${p.start_date || "…"} → ${p.end_date || "…"}` : ""}
                  </p>
                  <div className="mt-1 flex items-center gap-3 text-xs font-semibold text-slate-400 dark:text-slate-500">
                    <span className="inline-flex items-center gap-1" data-testid={`promo-views-${p.id}`}><Eye className="h-3.5 w-3.5" /> {p.views || 0} views</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5" /> {p.clicks || 0} clicks</span>
                  </div>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  {p.status === "pending" && <Button variant="outline" size="sm" onClick={() => approve(p)} data-testid={`promo-approve-${p.id}`} className="rounded-md border-emerald-200 font-semibold text-emerald-700 hover:bg-emerald-50"><Check className="mr-1.5 h-4 w-4" /> Approve</Button>}
                  {p.status === "pending" && <Button variant="outline" size="sm" onClick={() => reject(p)} data-testid={`promo-reject-${p.id}`} className="rounded-md border-slate-200 font-semibold text-slate-500 hover:bg-slate-50"><X className="mr-1.5 h-4 w-4" /> Reject</Button>}
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`promo-edit-${p.id}`} className="rounded-md font-semibold"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDel(p)} data-testid={`promo-delete-${p.id}`} className="rounded-md border-red-200 font-semibold text-red-600 hover:bg-red-50"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <AlertDialogContent data-testid="promo-dialog" className="max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{editing === "new" ? "New promotion" : "Edit promotion"}</AlertDialogTitle>
            <AlertDialogDescription>Set the offer and where it applies. Live status is time-boxed by the dates below.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Title *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="promo-title" className="mt-1.5" placeholder="e.g. 2.9% APR on all Kia" /></div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Presentation</Label>
              <Select value={form.display_format} onValueChange={(v) => set("display_format", v)}>
                <SelectTrigger className="mt-1.5" data-testid="promo-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text offer</SelectItem>
                  <SelectItem value="banner">3:1 banner special</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Type</Label>
                <Select value={form.promo_type} onValueChange={(v) => set("promo_type", v)}><SelectTrigger className="mt-1.5" data-testid="promo-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PROMO_TYPES).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Scope</Label>
                <Select value={form.scope} onValueChange={(v) => set("scope", v)}><SelectTrigger className="mt-1.5" data-testid="promo-scope"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="manufacturer">Manufacturer</SelectItem><SelectItem value="dealer">Dealer</SelectItem><SelectItem value="vehicle">Specific vehicles</SelectItem></SelectContent></Select>
              </div>
            </div>
            {form.scope === "manufacturer" && <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Make</Label><Input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="promo-make" className="mt-1.5" placeholder="e.g. Kia" /></div><div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Model (optional)</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="promo-model" className="mt-1.5" placeholder="e.g. Telluride" /></div></div>}
            {form.scope === "dealer" && (
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Dealer / partner</Label>
                <Select value={form.partner_id || ""} onValueChange={(v) => set("partner_id", v)}><SelectTrigger className="mt-1.5" data-testid="promo-partner"><SelectValue placeholder="Select a partner" /></SelectTrigger>
                  <SelectContent>{partners.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.dealership_name || p.name || p.email}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
            {form.scope === "vehicle" && <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Vehicle IDs (comma-separated)</Label><Input value={vehicleIdsStr} onChange={(e) => set("vehicle_ids", e.target.value)} data-testid="promo-vehicle-ids" className="mt-1.5" placeholder="id1, id2" /></div>}
            {form.display_format === "banner" && (
              <>
                <div>
                  <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">
                    Banner destination
                  </Label>
                  <Select
                    value={form.banner_destination}
                    onValueChange={(value) => set("banner_destination", value)}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="promo-banner-destination">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle">Selected vehicle</SelectItem>
                      <SelectItem value="inventory">Find a Car</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.banner_destination === "vehicle" && (
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">
                      Linked vehicle *
                    </Label>
                    <Select value={form.linked_vehicle_id} onValueChange={(v) => set("linked_vehicle_id", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="promo-banner-vehicle">
                        <SelectValue placeholder="Choose a vehicle" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map((vehicle) => (
                          <SelectItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.banner_hide_overlay}
                    onChange={(event) => set("banner_hide_overlay", event.target.checked)}
                    data-testid="promo-banner-hide-overlay"
                    className="h-4 w-4"
                  />
                  Show artwork without added text overlay
                </label>
              </>
            )}
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Badge label (optional)</Label><Input value={form.badge_label} onChange={(e) => set("badge_label", e.target.value)} data-testid="promo-badge" className="mt-1.5" placeholder="Defaults to the type name" /></div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Logo (optional — falls back to the brand mark)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="promo-logo-url" placeholder="Paste a logo URL, or upload" />
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <Button type="button" variant="outline" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="promo-logo-upload" className="shrink-0"><Upload className="mr-1.5 h-4 w-4" />{logoBusy ? "…" : "Upload"}</Button>
              </div>
              {form.logo_url && <img src={form.logo_url} alt="logo" className="mt-2 h-10 object-contain" />}
            </div>
            {form.display_format === "banner" && (
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">
                  Banner artwork *
                </Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    ref={bannerRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/avif"
                    className="hidden"
                    onChange={(event) => uploadBanner(event.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => bannerRef.current?.click()}
                    disabled={bannerBusy}
                    data-testid="promo-banner-upload"
                    className="shrink-0"
                  >
                    <Image className="mr-1.5 h-4 w-4" />
                    {bannerBusy ? "Uploading…" : "Upload artwork"}
                  </Button>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    JPG, PNG, WebP, or AVIF · Any ratio works
                  </span>
                </div>
                {form.banner_image_url && (
                  <img
                    src={form.banner_image_url}
                    alt="Banner artwork preview"
                    className="mt-3 aspect-[3/1] w-full border border-slate-200 object-cover dark:border-slate-800"
                    data-testid="promo-banner-preview"
                  />
                )}
              </div>
            )}
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="promo-desc" className="mt-1.5" placeholder="e.g. $2,000 cash back through month-end" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Start date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="promo-start" className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">End date</Label><Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="promo-end" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Priority</Label><Input type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} data-testid="promo-priority" className="mt-1.5" /></div>
              <label className="flex items-end gap-2 pb-2 text-sm font-semibold text-slate-700 dark:text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="promo-active" className="h-4 w-4" /> Active</label>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="promo-save" onClick={save} className="bg-rose-600 text-white hover:bg-rose-700">Save promotion</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="promo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this promotion?</AlertDialogTitle>
            <AlertDialogDescription>{confirmDel?.title} will be removed and its ribbons will disappear from cars.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promo-del-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="promo-del-confirm" onClick={() => confirmDel && del(confirmDel)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
